import { App, getContainer } from './WebServiceContainer';
import { installElectronAPIPolyfill } from './electronAPIPolyfill';
import { showInstancePickerIfNeeded } from './instancePicker';
import { installHistoryNavigation } from './instanceHistory';
import { installWebChrome } from './webChrome';
import { installWebSettings } from './webSettings';
import { installWebRotation } from './webRotation';
import { installWebIssues } from './webIssues';
import { installWebInstall } from './webInstall';
import { installWebFullscreen } from './webFullscreen';
import { installWakeLock } from './webWakeLock';
import { LogLevel, LogContext } from '@signage-player/contracts';

let signagePlayerStarted = false;

// Mirrors electron main.ts safeLog: never let logging break the handler.
function safeLog(level: LogLevel, message: string, context?: LogContext): void {
  try {
    App.logger().log(level, message, context);
  } catch {
    console.error(message, context);
  }
}

async function boot(): Promise<void> {
  installWebInstall();
  console.log('[Main] boot() starting');
  installWebChrome();
  installWebSettings({
    // The container doesn't exist until after the picker; the modal shows '—' until then.
    getAuthInstance: async () => {
      try {
        return await App.auth().getAuthInstance();
      } catch {
        return null;
      }
    },
  });
  installWebIssues();
  installWakeLock();
  installWebFullscreen();
  installHistoryNavigation();
  await showInstancePickerIfNeeded();
  // After the picker: the URL now names the instance whose cached display drives the rotation.
  installWebRotation();
  const container = getContainer().build();
  const logger = App.logger();
  logger.info('[Main] Starting web signage player', {
    services: container.getServiceNames(),
    version: App.config().version,
  });
  console.log('[Main] container built, services:', container.getServiceNames());

  // Install the electronAPI polyfill and load the renderer FIRST. The renderer
  // subscribes to broadcast events (player:initialize, auth-instance-update,
  // media:downloadProgress, …) during its constructor; if startApp ran before
  // those subscriptions existed, BrowserPlayer.initialize would broadcast
  // player:initialize into the void and the renderer would stay on the loader
  // forever waiting for an event that already fired.
  installElectronAPIPolyfill();
  console.log('[Main] electronAPI polyfill installed');
  await import('./renderer/renderer');
  console.log('[Main] renderer module imported');

  const auth = App.auth();
  const authInstance = await auth.getAuthInstance().catch(err => {
    logger.error('[Main] auth.getAuthInstance failed', { error: err as any });
    console.error('[Main] auth.getAuthInstance failed', err);
    return null;
  });
  console.log('[Main] initial authInstance:', authInstance?.toLogObject?.() ?? authInstance);

  const startApp = async (instance: typeof authInstance, additionalDelay = 0) => {
    console.log('[Main] startApp called, isPaired:', instance?.isPaired, 'hasToken:', !!instance?.token);
    if (!instance?.isPaired || !instance.token) return;
    if (signagePlayerStarted) return;
    signagePlayerStarted = true;
    try {
      console.log('[Main] running additionalBuild');
      container.additionalBuild(instance);
    } catch (err) {
      logger.critical('[Main] startApp failed', { error: err as any });
      console.error('[Main] startApp failed', err);
      return;
    }
    // Mirrors electron startSignagePlayer: player:initialize shows the main
    // container, so starting immediately after pairing would stomp the
    // finishing screen (spinner 5s → checkmark) mid-sequence.
    const startUpDelay = 3000 + additionalDelay;
    setTimeout(() => {
      console.log('[Main] running initSignageApplication');
      container
        .initSignageApplication(instance)
        .then(() => console.log('[Main] signage application initialized'))
        .catch(err => {
          logger.critical('[Main] initSignageApplication failed', { error: err as any });
          console.error('[Main] initSignageApplication failed', err);
        });
    }, startUpDelay);
  };

  if (authInstance?.isPaired && authInstance.token) {
    console.log('[Main] already paired, starting app directly');
    await startApp(authInstance);
  } else {
    console.log('[Main] not paired yet, activating polling');
    auth.activateAuthInstancePolling((newInstance, oldInstance) => {
      const changed = auth.checkIfAuthInstanceChanged(oldInstance, newInstance);
      console.log('[Main] poll tick, changed:', changed, 'newPaired:', newInstance?.isPaired);
      if (changed) {
        // 6s extra like electron: the just-paired finishing sequence runs
        // spinner 5s + checkmark before content may take the screen.
        startApp(newInstance, 6000);
      }
    }, 5000);
  }
}

const KNOWN_NOISE = [
  'PostHogFetchNetworkError',
  'ReadableStream is already closed',
  'AggregateError: Error',
  'Failed to fetch',
  'NetworkError when attempting to fetch',
  'Load failed',
];

window.addEventListener('error', e => {
  const text = String(e.error ?? e.message);
  const isKnown = KNOWN_NOISE.some(reason => text.includes(reason));
  safeLog(isKnown ? LogLevel.NOTICE : LogLevel.CRITICAL, '[Main] Uncaught error: ' + text, { error: e.error as any });
});

window.addEventListener('unhandledrejection', e => {
  const text = String(e.reason);
  const isKnown = KNOWN_NOISE.some(reason => text.includes(reason));
  safeLog(isKnown ? LogLevel.NOTICE : LogLevel.CRITICAL, '[Main] Unhandled rejection: ' + text, { reason: e.reason as any });
});

window.addEventListener('pagehide', () => {
  // Best-effort: push buffered IndexedDB/Sentry batches before teardown.
  try { void App.logger().flush(1000); } catch { /* ignore */ }
});

boot().catch(err => {
  console.error('[Main] Fatal boot error', err);
});
